import { POWERUP_RADIUS } from "../../shared/constants.js";
import { powerupDef, WEAPON_POWERUPS, type KillEvent, type PowerupType, type SnapshotDTO, type TankDTO } from "../../shared/protocol/index.js";
import { playSfx } from "../audio/index.js";
import { state } from "../state.js";
import type { Renderer } from "./Renderer.js";
import { addShake, flashVignette, fx, shakeFor, spawnBurst, spawnExplosionDebris, spawnSparks } from "./particles.js";
import { INTERP_DELAY, MUZZLE_MS, RAPIDFIRE_PEW_RATE } from "./types.js";
import { angleLerp, clamp01, lerp } from "./util.js";

/** Buffer a snapshot. Effects are NOT fired here — they're applied later, when
 *  the interpolation clock reaches this snapshot (see consumeEffects). */
export function push(rd: Renderer, snap: SnapshotDTO, nowMs: number): void {
  rd.buffer.push({ snap, recvAt: nowMs });
  if (rd.buffer.length > 30) rd.buffer.shift();
}

/**
 * Fire the transient effects (explosions, beams, deaths, kill events) of any
 * buffered snapshot the interpolation clock has now reached — i.e. delayed by
 * the same INTERP_DELAY as the rendered world — so they line up on screen.
 */
export function consumeEffects(rd: Renderer, target: number, nowMs: number): void {
  for (const { snap, recvAt } of rd.buffer) {
    if (recvAt <= rd.consumedUntil || recvAt > target) continue;
    detectTransients(rd, snap, nowMs);
    for (const b of snap.blasts) {
      rd.explosions.push({ x: b.x, y: b.y, color: "#e6863f", start: nowMs });
      spawnExplosionDebris(rd, b.x, b.y, "#e6863f", nowMs);
      addShake(rd, shakeFor(snap, b.x, b.y, 5));
      playSfx("explosion", 0.5);
    }
    for (const bm of snap.beams) {
      rd.beams.push({ x1: bm.x1, y1: bm.y1, x2: bm.x2, y2: bm.y2, start: nowMs });
      // Impact sparks where the beam terminates.
      spawnSparks(rd, bm.x2, bm.y2, "#c9a6ff", 6, nowMs);
      playSfx("pew_laser", 0.3);
    }
    if (snap.events.length) rd.pendingEvents.push(...snap.events);
    rd.consumedUntil = recvAt;
  }
}

/** Drain kill-log events that have reached the interpolation clock. */
export function takeEvents(rd: Renderer): KillEvent[] {
  if (rd.pendingEvents.length === 0) return [];
  const out = rd.pendingEvents;
  rd.pendingEvents = [];
  return out;
}

/** Spawn an explosion wherever a tank died, and play sounds for firing. */
function detectTransients(rd: Renderer, snap: SnapshotDTO, nowMs: number): void {
  const seen = new Set<string>();
  // New bullets this frame, per owner — catches rapid-fire's scheduled
  // shots, which add a bullet without an ammo/weaponCharges change.
  const newBulletsByOwner = new Map<string, number>();
  for (const b of snap.bullets) {
    if (rd.lastBulletIds.has(b.id)) continue;
    newBulletsByOwner.set(b.ownerId, (newBulletsByOwner.get(b.ownerId) ?? 0) + 1);
  }
  rd.lastBulletIds = new Set(snap.bullets.map((b) => b.id));
  for (const t of snap.tanks) {
    seen.add(t.id);
    const prev = rd.lastTankState.get(t.id);
    if (prev && prev.alive) {
      if (!t.alive) {
        rd.explosions.push({ x: prev.x, y: prev.y, color: t.color, start: nowMs });
        spawnExplosionDebris(rd, prev.x, prev.y, t.color, nowMs);
        if (t.id === state.playerId) {
          addShake(rd, 8);
          flashVignette(true);
        } else {
          addShake(rd, shakeFor(snap, prev.x, prev.y, 4));
        }
        playSfx("explosion", 0.5);
      } else {
        if (prev.hp !== undefined && t.hp < prev.hp) {
          fx(rd, t.id).hitAt = nowMs;
          spawnSparks(rd, t.x, t.y, "#ffffff", 5, nowMs);
          if (t.id === state.playerId) {
            addShake(rd, 3);
            flashVignette(false);
          }
          playSfx("oof", 0.4);
        }

        // Play firing sound if ammo dropped, or if they reloaded and fired in the same snapshot
        const chargeSum = Object.values(t.weaponCharges).reduce((a, n) => a + (n ?? 0), 0);
        const firedNormal = t.ammo < prev.ammo || (prev.ammo === 0 && t.ammo > 0 && t.ammo < t.maxAmmo);
        const firedSpecialRaw = !t.charging && chargeSum < prev.weaponChargeSum && prev.weaponChargeSum > 0;
        // A held laser with no windup delay consumes its charge and emits its
        // beam in the very same snapshot — the beams loop above already plays
        // a pew per beam, so skip this one to avoid a double "shoot" sound.
        const hadLaser = (prev.weaponCharges.laser ?? 0) > 0;
        const firedSpecial = firedSpecialRaw && !hadLaser;
        const isRapid = prev.hadRapid;
        const rapidFireRate = isRapid ? RAPIDFIRE_PEW_RATE : 1.0;

        let sfxName = "pew";
        if (firedSpecial) {
          for (const w of WEAPON_POWERUPS) {
            const prevCount = prev.weaponCharges[w] ?? 0;
            const currCount = t.weaponCharges[w] ?? 0;
            if (currCount < prevCount) {
              sfxName = "pew_" + w;
              break;
            }
          }
        } else if (isRapid) {
          sfxName = "pew_rapidfire";
        }

        if (firedNormal || firedSpecial) {
          playSfx(sfxName, 0.3, rapidFireRate);
          // A rapid-fire click can share a frame with its first scheduled
          // shot(s) when the delay is shorter than the snapshot interval; sound
          // those extras too. (Multishot fires one logical volley, so its extra
          // pellets are intentionally left as a single pew.)
          if (isRapid) {
            const extra = (newBulletsByOwner.get(t.id) ?? 0) - 1;
            for (let i = 0; i < extra; i++) playSfx("pew_rapidfire", 0.3, RAPIDFIRE_PEW_RATE);
          }
        } else {
          // A scheduled rapid-fire shot: a new bullet appeared for this tank
          // with no ammo/charge change to explain it (see newBulletsByOwner).
          const echoes = newBulletsByOwner.get(t.id) ?? 0;
          const echoSfx = isRapid ? "pew_rapidfire" : "pew";
          for (let i = 0; i < echoes; i++) playSfx(echoSfx, 0.3, RAPIDFIRE_PEW_RATE);
        }

        // Any shot this snapshot: flash the muzzle and kick the hull back.
        if (firedNormal || firedSpecial || (newBulletsByOwner.get(t.id) ?? 0) > 0) {
          const f = fx(rd, t.id);
          f.muzzleUntil = nowMs + MUZZLE_MS;
          f.recoilAt = nowMs;
        }

        if (prev.reloadIn === 0 && t.reloadIn > 0) {
          playSfx("reloading", 0.4);
        }

        if (!prev.charging && t.charging) {
          console.log(`[Laser SFX] Triggered for tank ${t.id}. Local: ${t.id === state.playerId}`);
          if (t.id === state.playerId) {
            playSfx("laser_charge", 0.6);
          } else {
            const me = snap.tanks.find((tk) => tk.id === state.playerId);
            let vol = 0.25;
            if (me) {
              const dx = me.x - t.x;
              const dy = me.y - t.y;
              const dist = Math.hypot(dx, dy);
              vol = Math.max(0.05, 0.3 * (1 - dist / 800));
            }
            playSfx("laser_charge", vol);
          }
        }
      }
    } else if (prev && !prev.alive && t.alive) {
      // Respawn: pop-in scale (drawTank) + a contracting ring in the tank's color.
      fx(rd, t.id).spawnAt = nowMs;
      rd.rings.push({
        x: t.x,
        y: t.y,
        color: t.color,
        born: nowMs,
        life: 450,
        r0: rd.tankR * 2.4,
        r1: rd.tankR * 0.9,
        width: 2.5,
      });
    }
    rd.lastTankState.set(t.id, {
      x: t.x, y: t.y, alive: t.alive, hp: t.hp, color: t.color,
      ammo: t.ammo,
      weaponChargeSum: Object.values(t.weaponCharges).reduce((a, n) => a + (n ?? 0), 0),
      hadRapid: (t.weaponCharges.rapidfire ?? 0) > 0,
      reloadIn: t.reloadIn, boosted: t.boosted, shielded: t.shielded, scoped: t.scoped,
      charging: t.charging,
      weaponCharges: { ...t.weaponCharges }
    });
  }
  // A previously-alive tank that vanished (killed while disconnected) also pops.
  for (const [id, prev] of rd.lastTankState) {
    if (!seen.has(id)) {
      if (prev.alive) {
        rd.explosions.push({ x: prev.x, y: prev.y, color: prev.color, start: nowMs });
        spawnExplosionDebris(rd, prev.x, prev.y, prev.color, nowMs);
        playSfx("explosion", 0.5);
      }
      rd.lastTankState.delete(id);
      rd.tankFx.delete(id);
      rd.lastDust.delete(id);
      rd.lastTreadPos.delete(id);
      rd.treadPhase.delete(id);
    }
  }

  let pickedUpType: PowerupType | null = null;
  const currentPowerupIds = new Set(snap.powerups.map(p => p.id));
  for (const p of rd.lastPowerups.values()) {
    if (!currentPowerupIds.has(p.id)) {
      // Powerup disappeared. Was it picked up by the local player?
      const me = snap.tanks.find(t => t.id === state.playerId);
      if (me && me.alive) {
        const dx = me.x - p.x;
        const dy = me.y - p.y;
        if (dx * dx + dy * dy <= 1600) { // 40 radius squared, for some interpolation margin
          pickedUpType = p.type;
        }
      }
      // Anyone within pickup range grabbed it → colored burst + ring; a crate
      // that timed out with nobody nearby just dissolves into a faint puff.
      const grabbed = snap.tanks.some(
        (t) => t.alive && (t.x - p.x) ** 2 + (t.y - p.y) ** 2 <= 1600
      );
      const color = powerupDef(p.type).color;
      if (grabbed) {
        spawnSparks(rd, p.x, p.y, color, 8, nowMs);
        rd.rings.push({
          x: p.x,
          y: p.y,
          color,
          born: nowMs,
          life: 380,
          r0: POWERUP_RADIUS * 0.6,
          r1: POWERUP_RADIUS * 2.6,
          width: 2.5,
        });
      } else {
        spawnBurst(rd, p.x, p.y, 3, nowMs, {
          color: "#9b9282",
          speed: 16,
          size: 3,
          life: 500,
          alpha: 0.35,
          drag: 1,
          grow: 2,
        });
      }
    }
  }
  rd.lastPowerups.clear();
  for (const p of snap.powerups) {
    rd.lastPowerups.set(p.id, p);
  }
  if (pickedUpType) {
    playSfx("pickup_" + pickedUpType, 0.6);
  }
}

/** Linearly interpolate tank/bullet positions ~INTERP_DELAY ms in the past. */
export function interpolated(rd: Renderer, nowMs: number): SnapshotDTO | null {
  if (rd.buffer.length === 0) return null;
  if (rd.buffer.length === 1) return rd.buffer[0].snap;

  const target = nowMs - INTERP_DELAY;
  let a = rd.buffer[0];
  let b = rd.buffer[rd.buffer.length - 1];
  for (let i = 0; i < rd.buffer.length - 1; i++) {
    if (rd.buffer[i].recvAt <= target && rd.buffer[i + 1].recvAt >= target) {
      a = rd.buffer[i];
      b = rd.buffer[i + 1];
      break;
    }
  }
  const span = b.recvAt - a.recvAt;
  const f = span > 0 ? clamp01((target - a.recvAt) / span) : 1;

  const bById = new Map(a.snap.tanks.map((t) => [t.id, t]));
  const tanks: TankDTO[] = b.snap.tanks.map((tb) => {
    const ta = bById.get(tb.id);
    if (!ta) return tb;
    return {
      ...tb,
      x: lerp(ta.x, tb.x, f),
      y: lerp(ta.y, tb.y, f),
      bodyAngle: angleLerp(ta.bodyAngle, tb.bodyAngle, f),
      turretAngle: angleLerp(ta.turretAngle, tb.turretAngle, f),
    };
  });

  const aBullets = new Map(a.snap.bullets.map((bl) => [bl.id, bl]));
  const bullets = b.snap.bullets.map((bb) => {
    const ba = aBullets.get(bb.id);
    if (!ba) return bb;
    // Travel direction from the movement between the two samples (for tracking).
    const dir = bb.x !== ba.x || bb.y !== ba.y ? Math.atan2(bb.y - ba.y, bb.x - ba.x) : bb.dir;
    return { ...bb, x: lerp(ba.x, bb.x, f), y: lerp(ba.y, bb.y, f), dir };
  });

  // Flags move with their carrier, so interpolate them on the same clock.
  const aFlags = new Map(a.snap.flags.map((fl) => [fl.team, fl]));
  const flags = b.snap.flags.map((fb) => {
    const fa = aFlags.get(fb.team);
    if (!fa) return fb;
    return { ...fb, x: lerp(fa.x, fb.x, f), y: lerp(fa.y, fb.y, f) };
  });

  return { t: target, tanks, bullets, powerups: [], flags, blasts: [], beams: [], events: [], wallHp: [] };
}
